import { Overlay, OverlayPositionBuilder } from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";
import { DestroyRef, Directive, ElementRef, inject, input } from "@angular/core";
import { Tooltip } from "@ui/tooltip";
import { tw } from "@ui/utils/tailwind-template-literal-tag";

export const tooltipClass = tw`underline decoration-table-header decoration-dotted decoration-2 underline-offset-4`;

@Directive({
  selector: "[tooltip]",
  host: {
    "[class]": "tooltipClass",
    "(document:keydown.escape)": "hide()",
    "(mouseenter)": "show()",
    "(mouseleave)": "hide()",
  },
})
export class TooltipDirective {
  // Dependencies
  readonly #overlay = inject(Overlay);
  readonly #overlayPositionBuilder = inject(OverlayPositionBuilder);
  readonly #elementRef = inject(ElementRef);
  readonly #destroyRef = inject(DestroyRef);

  // Variables
  // Only apply tooltip styles to non-interactive elements to avoid conflicts with existing styles on interactive elements like buttons or links
  readonly tooltipClass = (() => {
    const tag = String(this.#elementRef.nativeElement.tagName).toLowerCase();
    const interactiveElements = ["button", "input", "select", "textarea", "a"];
    return interactiveElements.includes(tag) ? "" : tooltipClass;
  })();
  readonly #tooltipElement = this.#overlay.create({
    positionStrategy: this.#overlayPositionBuilder
      .flexibleConnectedTo(this.#elementRef)
      .withPositions([
        {
          originX: "center",
          originY: "top",
          overlayX: "center",
          overlayY: "bottom",
        },
      ]),
  });

  // Inputs
  readonly value = input<string | undefined | null>("", {
    alias: "tooltip",
  });

  constructor() {
    this.#destroyRef.onDestroy(() => {
      this.#tooltipElement.dispose();
    });
  }

  show() {
    const tooltipPortal = new ComponentPortal(Tooltip);

    if (!this.#tooltipElement.hasAttached()) {
      this.#tooltipElement.attach(tooltipPortal).instance.value.set(this.value());
    }
  }

  hide() {
    this.#tooltipElement.detach();
  }
}
